/**
 * Course Translation Service
 *
 * Frontend service for the course translation feature: translate an existing
 * (master) course into another language as a new, linked course document.
 *
 * The API returns a fuller picture than this panel needs — per-module progress,
 * both course-level steps, every failed piece. Rather than renaming API fields
 * to suit one screen (which would degrade the contract for every other caller),
 * the reshaping happens here. Adapting between a server contract and a screen
 * is exactly this layer's job.
 */

import apiClient from './api';

// TODO: fold into API_ENDPOINTS in src/config/api.config.ts.
const ENDPOINTS = {
  LIST: (courseId: string) => `/api/courses/${courseId}/translations`,
  CREATE: (courseId: string) => `/api/courses/${courseId}/translations`,
  RETRY: (courseId: string, lang: string) => `/api/courses/${courseId}/translations/${lang}/retry`,
  DELETE: (courseId: string, lang: string) => `/api/courses/${courseId}/translations/${lang}`,
  DETAIL: (courseId: string, lang: string) => `/api/courses/${courseId}/translations/${lang}`,
  SYNC: (courseId: string, lang: string) => `/api/courses/${courseId}/translations/${lang}/sync`,
  GLOSSARY: (courseId: string, lang: string) => `/api/courses/${courseId}/translations/${lang}/glossary`,
};

// ---------------------------------------------------------------------------
// Types (shape this panel renders — see the adapters for the API mapping)
// ---------------------------------------------------------------------------

export type TranslationStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'FAILED'
  | 'PARTIALLY_FAILED';

export interface TranslationProgress {
  current_module: number;
  total_modules: number;
  lessons_done: number;
  lessons_total: number;
  audio_done: number;
  audio_total: number;
  /** Video courses only. Both stay 0 for an HTML course. */
  videos_done: number;
  videos_total: number;
  failed_pieces: number;
}

export interface CourseTranslationSummary {
  /** _id of the translated course document (null until the clone exists) */
  translated_course_id: string | null;
  /** BCP-47 target, e.g. 'hi-IN' */
  language_code: string;
  status: TranslationStatus;
  progress: TranslationProgress;
  /** true when the master has been edited after this translation was made */
  is_stale: boolean;
  /** When this language began translating. Absent on a queued language. */
  started_at?: string;
  translated_at?: string;
  error?: string;
  /**
   * The specific lessons that failed, so the admin can see WHAT needs
   * attention instead of only a count. Populated when failed_pieces > 0.
   */
  failed_items?: Array<{ module: string; lesson: string; reason?: string }>;
}

/**
 * What an Update actually did. The backend has always returned this from POST
 * /sync — the full drift plus counts — and the UI threw the whole response away
 * and showed a fixed "Translation update started".
 */
export interface TranslationUpdateResult {
  /** Nothing had actually changed; the version was stamped and no AI ran. */
  already_current: boolean;
  /** Lessons whose text differs from the master since this language was built. */
  changed_lessons: number;
  /** Lessons added to the master afterwards, which had no translated copy. */
  added_lessons: number;
  /** Translated copies of lessons since deleted from the master. */
  removed_lessons: number;
  /** Modules actually queued for re-translation — the unit of work. */
  modules_requeued: number;
  /** Title / description / objectives / course-level assessments re-queued. */
  course_details_requeued: boolean;
}

export interface StartTranslationInput {
  language_code: string;
  /** Display name stored on the translated course, e.g. 'HINDI'. */
  language_name: string;
}

/** One agreed term. Most are identity mappings — see the glossary endpoint. */
export interface GlossaryEntry {
  source_term: string;
  target_term: string;
  /** True when the term deliberately stays in the source language. */
  keep_source: boolean;
}

// ---------------------------------------------------------------------------
// API shapes and adapters
// ---------------------------------------------------------------------------

interface ApiTranslation {
  translation_id: string;
  source_course_id: string;
  translated_course_id: string;
  target_language_code: string;
  overall_status: TranslationStatus;
  is_out_of_date: boolean;
  is_visible_to_learners: boolean;
  translated_by?: string;
  started_at?: string;
  completed_at?: string;
  pieces_done: number;
  pieces_total: number;
  pieces_failed: number;
  videos_done?: number;
  videos_total?: number;
  /** Modules settled / total. Sent on the list response so the badge is real. */
  modules_done?: number;
  modules_total?: number;
}

interface ApiTranslationDetail extends ApiTranslation {
  module_statuses: Array<{
    module_id: string;
    title?: string;
    status: TranslationStatus;
    lessons_done: number;
    lessons_total: number;
    audio_done: number;
    audio_total: number;
    assessments_done: number;
    assessments_total: number;
    scenes_done?: number;
    scenes_total?: number;
    videos_done?: number;
    videos_total?: number;
    failed_pieces: number;
    failed_items: Array<{ entity_id: string; lesson?: string; reason: string }>;
  }>;
  course_details_status: TranslationStatus;
  course_assessments_status: TranslationStatus;
}

interface ApiSyncResult {
  already_current: boolean;
  modules_requeued: number;
  course_level_requeued: boolean;
  orphans_removed: number;
  drift?: {
    changed_content_item_ids?: string[];
    added_content_item_ids?: string[];
    orphaned_content_item_ids?: string[];
    modules_to_retranslate?: string[];
    course_details_changed?: boolean;
  };
}

/** POST /retry answers a narrower question: what got re-queued. */
interface ApiRetryResult {
  modules_requeued: number;
  course_level_requeued: boolean;
}

interface ApiTranslationList {
  translations: ApiTranslation[];
  available_language_codes: string[];
  pending_language_codes: string[];
}

/** Unwrap the ResultEntity envelope the SRV layer wraps every response in. */
const unwrap = <T,>(response: any): T => response.data?.data ?? response.data;

/**
 * List rows carry no per-module breakdown, so module counts are unknown here.
 * Reporting 0/0 would render an empty bar on a healthy run, so the aggregate
 * piece counts stand in — which is what the list actually displays. The detail
 * endpoint supplies the real per-module numbers.
 */
function toSummary(row: ApiTranslation): CourseTranslationSummary {
  return {
    translated_course_id: row.translated_course_id || null,
    language_code: row.target_language_code,
    status: row.overall_status,
    progress: {
      // Real module counts now come from the list response. They used to be
      // hardcoded 0, which rendered "module 1/0" on a ten-module course.
      current_module: row.modules_done ?? 0,
      total_modules: row.modules_total ?? 0,
      // These are PIECE counts — lessons + audio + assessments — not lessons.
      lessons_done: row.pieces_done,
      lessons_total: row.pieces_total,
      audio_done: 0,
      audio_total: 0,
      // Sent by the list response on a video course, so the bar can say
      // "Videos 12/45" instead of an opaque piece count.
      videos_done: row.videos_done ?? 0,
      videos_total: row.videos_total ?? 0,
      failed_pieces: row.pieces_failed,
    },
    is_stale: row.is_out_of_date,
    started_at: row.started_at,
    translated_at: row.completed_at,
  };
}

/** Detail rows have everything, including which lessons failed and why. */
function toSummaryFromDetail(row: ApiTranslationDetail): CourseTranslationSummary {
  const modules = row.module_statuses || [];
  const sum = (pick: (m: (typeof modules)[number]) => number) =>
    modules.reduce((total, m) => total + (pick(m) || 0), 0);

  const failed_items = modules.flatMap((m) =>
    (m.failed_items || []).map((item) => ({
      module: m.title || m.module_id,
      lesson: item.lesson || item.entity_id,
      reason: item.reason,
    }))
  );

  return {
    translated_course_id: row.translated_course_id || null,
    language_code: row.target_language_code,
    status: row.overall_status,
    progress: {
      current_module: modules.filter((m) => m.status === 'COMPLETED').length,
      total_modules: modules.length,
      lessons_done: sum((m) => m.lessons_done),
      lessons_total: sum((m) => m.lessons_total),
      audio_done: sum((m) => m.audio_done),
      audio_total: sum((m) => m.audio_total),
      videos_done: sum((m) => m.videos_done ?? 0),
      videos_total: sum((m) => m.videos_total ?? 0),
      failed_pieces: sum((m) => m.failed_pieces),
    },
    is_stale: row.is_out_of_date,
    started_at: row.started_at,
    translated_at: row.completed_at,
    failed_items: failed_items.length ? failed_items : undefined,
  };
}

function toUpdateResult(row: ApiSyncResult): TranslationUpdateResult {
  const drift = row?.drift ?? {};
  return {
    already_current: Boolean(row?.already_current),
    changed_lessons: drift.changed_content_item_ids?.length ?? 0,
    added_lessons: drift.added_content_item_ids?.length ?? 0,
    removed_lessons: drift.orphaned_content_item_ids?.length ?? 0,
    modules_requeued: row?.modules_requeued ?? 0,
    course_details_requeued: Boolean(drift.course_details_changed),
  };
}

/**
 * A retry knows what it re-queued but not WHY — there is no drift, because
 * nothing changed in the master. Reported as zero changed lessons rather than
 * left undefined, so the caller has one shape to render.
 */
function toRetryResult(row: ApiRetryResult): TranslationUpdateResult {
  return {
    already_current: false,
    changed_lessons: 0,
    added_lessons: 0,
    removed_lessons: 0,
    modules_requeued: row?.modules_requeued ?? 0,
    course_details_requeued: Boolean(row?.course_level_requeued),
  };
}

/**
 * Turn an update result into the sentence the admin reads. Named counts, never
 * a bare "started".
 */
export function describeUpdateResult(result: TranslationUpdateResult): string {
  if (result.already_current) {
    return 'Already up to date — nothing needed re-translating.';
  }

  const parts: string[] = [];
  const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

  if (result.changed_lessons > 0) parts.push(`${plural(result.changed_lessons, "edited lesson")}`);
  if (result.added_lessons > 0) parts.push(`${plural(result.added_lessons, "new lesson")}`);
  if (result.course_details_requeued) parts.push("course details");

  // A retry has no drift to describe, so fall back to the unit of work.
  if (parts.length === 0 && result.modules_requeued > 0) {
    parts.push(`${plural(result.modules_requeued, "module")}`);
  }

  const removed =
    result.removed_lessons > 0
      ? ` ${plural(result.removed_lessons, "deleted lesson")} removed.`
      : "";

  if (parts.length === 0) {
    return `Update queued.${removed}`;
  }

  const scope =
    result.modules_requeued > 0
      ? ` (${plural(result.modules_requeued, "module")} re-run, the rest untouched)`
      : "";

  return `Re-translating ${parts.join(", ")}${scope}.${removed}`;
}

// ---------------------------------------------------------------------------
// Timing
// ---------------------------------------------------------------------------

/**
 * A duration, in the largest two units that carry information. 42s, 6m 12s, 1h
 * 4m.
 */
function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

/** Wall-clock time of day, e.g. "14:32". Date is carried separately. */
function formatClock(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

/** One line describing when a translation ran and how long it took. */
export function describeTranslationTiming(
  row: Pick<CourseTranslationSummary, `started_at` | `translated_at` | `status`>,
  now: number = Date.now()
): string {
  if (!row.started_at) return ``;

  const started = new Date(row.started_at).getTime();
  if (Number.isNaN(started)) return ``;

  const start_clock = formatClock(row.started_at);

  if (row.translated_at) {
    const finished = new Date(row.translated_at).getTime();
    if (!Number.isNaN(finished) && finished >= started) {
      // Both ends known: the number the admin actually wants for the NEXT
      // course is the elapsed total, so it leads.
      return `${start_clock} \u2192 ${formatClock(row.translated_at)} \u00b7 took ${formatDuration(finished - started)}`;
    }
  }

  const running = row.status === 'PENDING' || row.status === 'IN_PROGRESS';
  const elapsed = formatDuration(now - started);

  // Still going, or stopped without ever recording an end. Both are honest
  // about what is known: when it began, and how long that has been.
  return running
    ? `Started ${start_clock} \u00b7 running ${elapsed}`
    : `Started ${start_clock} \u00b7 stopped after ${elapsed}`;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class CourseTranslationService {
  /** List all language versions of a master course, including queued languages, with live progress. */
  async getTranslations(courseId: string): Promise<CourseTranslationSummary[]> {
    const response = await apiClient.get(ENDPOINTS.LIST(courseId));
    const body = unwrap<ApiTranslationList>(response);
    const activeRows = (body?.translations || []).map(toSummary);
    const existingCodes = new Set(activeRows.map((r) => r.language_code));

    const queuedRows: CourseTranslationSummary[] = (body?.pending_language_codes || [])
      .filter((code) => !existingCodes.has(code))
      .map((code) => ({
        translated_course_id: null,
        language_code: code,
        status: 'PENDING',
        progress: {
          current_module: 0,
          total_modules: 0,
          lessons_done: 0,
          lessons_total: 0,
          audio_done: 0,
          audio_total: 0,
          videos_done: 0,
          videos_total: 0,
          failed_pieces: 0,
        },
        is_stale: false,
      }));

    return [...activeRows, ...queuedRows];
  }

  /**
   * Rows PLUS the languages still queued behind them. A queued language has no
   * tracking record yet, so it cannot appear in the rows — but omitting it
   * makes a not-yet-started language look like one that failed.
   */
  async getTranslationsWithQueue(
    courseId: string
  ): Promise<{ rows: CourseTranslationSummary[]; queued: string[] }> {
    const response = await apiClient.get(ENDPOINTS.LIST(courseId));
    const body = unwrap<ApiTranslationList>(response);
    return {
      rows: (body?.translations || []).map(toSummary),
      queued: body?.pending_language_codes || [],
    };
  }

  /**
   * Full progress for one language, including which lessons failed. Poll THIS
   * rather than the list while a run is in flight — the list has no per-module
   * breakdown, so a progress bar built from it cannot show "4 of 6 modules".
   */
  async getTranslationDetail(
    courseId: string,
    languageCode: string
  ): Promise<CourseTranslationSummary> {
    const response = await apiClient.get(ENDPOINTS.DETAIL(courseId, languageCode));
    return toSummaryFromDetail(unwrap<ApiTranslationDetail>(response));
  }

  /** Start translating the master course into a new language (202-style). */
  async startTranslation(
    courseId: string,
    input: StartTranslationInput
  ): Promise<CourseTranslationSummary> {
    // The API takes a LIST. Sequencing several languages is a backend concern —
    // launched together they exceed the AI provider's rate limit and fail — so
    // one language is simply a list of one.
    await apiClient.post(ENDPOINTS.CREATE(courseId), {
      languages: [{ language_code: input.language_code, language_name: input.language_name }],
    });

    // The response reports what STARTED versus what was QUEUED, not a row.
    const rows = await this.getTranslations(courseId);
    const created = rows.find((r) => r.language_code === input.language_code);
    if (created) return created;

    return {
      translated_course_id: null,
      language_code: input.language_code,
      status: 'PENDING',
      progress: {
        current_module: 0, total_modules: 0,
        lessons_done: 0, lessons_total: 0,
        audio_done: 0, audio_total: 0,
        videos_done: 0, videos_total: 0,
        failed_pieces: 0,
      },
      is_stale: false,
    };
  }

  /**
   * Retry only the failed pieces of a translation, or re-run a stale one
   * against the latest master (both are "fill the same document again").
   */
  async retryTranslation(
    courseId: string,
    languageCode: string
  ): Promise<TranslationUpdateResult> {
    // Two different operations, deliberately kept apart on the backend: stale
    // -> SYNC re-translates only what CHANGED since the last run failed ->
    // RETRY re-queues only what FAILED Calling retry on an up-to-date run is
    const current = await this.getTranslationDetail(courseId, languageCode);
    const isSync = current.is_stale && current.progress.failed_pieces === 0;
    const endpoint = isSync
      ? ENDPOINTS.SYNC(courseId, languageCode)
      : ENDPOINTS.RETRY(courseId, languageCode);

    const response = await apiClient.post(endpoint);

    return isSync
      ? toUpdateResult(unwrap<ApiSyncResult>(response))
      : toRetryResult(unwrap<ApiRetryResult>(response));
  }

  /**
   * Bring a language back in step with the master after an edit. Re-translates
   * only what changed — a one-word fix re-runs that lesson's module, not the
   * course.
   */
  async syncTranslation(courseId: string, languageCode: string): Promise<TranslationUpdateResult> {
    const response = await apiClient.post(ENDPOINTS.SYNC(courseId, languageCode));
    return toUpdateResult(unwrap<ApiSyncResult>(response));
  }

  /**
   * The agreed terminology for one language. Decided once before translation
   * and applied to every module — this is what stops parallel modules rendering
   * the same term three different ways.
   */
  async getGlossary(courseId: string, languageCode: string): Promise<GlossaryEntry[]> {
    const response = await apiClient.get(ENDPOINTS.GLOSSARY(courseId, languageCode));
    return unwrap<{ glossary: GlossaryEntry[] }>(response)?.glossary || [];
  }

  /** Correct the terminology, then apply it. */
  async replaceGlossary(
    courseId: string,
    languageCode: string,
    glossary: GlossaryEntry[]
  ): Promise<GlossaryEntry[]> {
    const response = await apiClient.put(ENDPOINTS.GLOSSARY(courseId, languageCode), { glossary });
    const saved = unwrap<{ glossary: GlossaryEntry[] }>(response)?.glossary || [];
    await this.syncTranslation(courseId, languageCode);
    return saved;
  }

  /** Delete a language version (soft delete on the backend). */
  async deleteTranslation(courseId: string, languageCode: string): Promise<void> {
    await apiClient.delete(ENDPOINTS.DELETE(courseId, languageCode));
  }
}

export const courseTranslationService = new CourseTranslationService();
export default courseTranslationService;
